import * as Actions from '../actions/alertActions';
import Alert from '../../lib/alert';
import {captureMessage} from '../../lib/picos/error';

export type AlertState = Alert[];

export type AlertAction = {
	type: string;
	alert: Alert;
	alerts: Array<Alert>;
}

/**
 * Count the number of times an alert shows up in an array of alerts.
 * Use the hash property of the alerts to see if they are the same.
 *
 * @param {Alert} alert
 * @param {Array} alerts
 * @return {number}
 */
function count(alert: Alert, alerts: Array<Alert>): number {
	const hashes = alerts.map(a => a.hash);
	const hash = alert.hash;

	return hashes.filter(h => h === hash).length;
}

/**
 * Remove all duplicates from an array of Alerts, based on their hashes.
 *
 * @param {Array.<Alert>} alerts
 */
function removeDuplicates(alerts: Alert[]): Alert[] {
	const m = new Map(); // Checked
	alerts.forEach(a => m.set(a.hash, a));
	const entries: Alert[] = Array.from(m.values());
	m.clear();

	return entries;
}

/**
 * Alerts to show on the screen.
 *
 * @param {Array.<Alert>} state
 * @param {AlertAction} action
 */
function alert(state: AlertState = [], action: AlertAction): Alert[] {
	let st = [...state];

	switch (action.type) {

		case Actions.ALERT_ADD: {
			// only add if unique!
			if(count(action.alert, st) === 0) {
				if ([Alert.WARNING, Alert.ERROR].includes(action.alert.type)) {
					captureMessage(action.alert.message);
				}
				st.push(action.alert);
			}

			return st;
		}

		case Actions.ALERT_ADD_MULTIPLE: {
			// Get rid of duplicates in the input alerts
			return removeDuplicates(st.concat(action.alerts));
		}

		case Actions.ALERT_REMOVE: {
			// Remove from UI
			return st.filter(a => action.alert.hash !== a.hash);
		}

		case Actions.ALERT_REMOVE_MULTIPLE: {
			// Remove multiple alerts from the UI.  Because we do not allow duplicates in the master
			// array, it is safe to assume that state has no dupes.  So we just need to remove each
			// of the action.alerts from the array.
			const toRemoveHashes = action.alerts.map(a => a.hash);

			return st.filter(a => {
				return !toRemoveHashes.includes(a.hash);
			});
		}

		default:
			return state;
	}
}

export default alert;
